import { iono } from 'global';
import { TouchTap, delegate, resizeObserver, query } from '../../utils/dom';
import { supportsMouseInput, supportsTouchInput } from '../utils/support';
import Theme from '../utils/theme';
import { createTooltip, destroyTooltip } from '../utils/tooltip';
import Locale from '../utils/locale';
import logo from '../../assets/logo.svg';
import externalLink from '../assets/icons/external-link.svg';
import { isIframe } from '../../../player/utils/environment';

function buildThemeOptions (options) {
  const colors = {};

  // pick out the theme color options from the ui options
  for (const color of ['text', 'background', 'primary', 'border']) {
    if (typeof options[color] !== 'undefined') {
      colors[color] = options[color];
    }
  }

  if (typeof options.accent !== 'undefined') {
    colors.primary = options.accent;
  }

  return {
    rounded: options.rounded >= 0 ? options.rounded : null,
    colors: colors
  };
}

export default class Layout {
  constructor (player, options) {
    this.options = options;
    this.player = player;
    this.locale = Locale(player.locale);
    this._tooltipsInitialized = false;

    // this.init(this.options);

    this.ontooltipshow = delegate('[title]', this.ontooltipshow.bind(this));
    this.onresize = this.onresize.bind(this);
  }

  $ (selector) {
    if (this.$element) {
      return query(selector, this.$element);
    }
  }

  isAttached () {
    return !!this.$element;
  }

  usesTooltips () {
    return true;
  }

  init (options) {
    this.theme = new Theme(this, buildThemeOptions(options.theme));
  }

  getAdContainer () {
    return this.$('.iono-ima-container');
  }

  getAdCustomClickTracker () {
    return this.$('.iono-ima-clicktracker');
  }

  getTheme () {
    return this.theme;
  }

  setTheme (options) {
    options = buildThemeOptions(options);

    if (options.rounded !== null) { this.theme.setRounded(options.rounded, false); }

    this.theme.setColors(options.colors, false);
    this.theme.update();

    return this;
  }

  /**
   * Handler for layout resize events
   *
   * @param {CustomEvent} event A custom resize event triggered by the ResizeObserver API
   */
  onresize (event) {}

  onstate (event) {
    if (this.$element) {
      this.$element.setAttribute('data-state', event.currentState);
    }
  }

  ontooltipshow (event) {
    createTooltip(event.delegateTarget, {
      // container: isIframe() ? this.$element : document.body,
      container: this.$element,
      destroyOnHide: true
      // placement: 'top'
    }).show(event);
  }

  onplaylistchange (event) {
    // if (event.playlist.metadata.url) {
    //   this.$('.iono-brand').outerHTML = this.renderBranding(event.playlist.metadata.url);
    // }
  }

  onplaylistselect (event) {
    if (event.playlistItem.metadata.url) {
      const $brand = this.$('.iono-brand');
      if ($brand) $brand.outerHTML = this.renderBranding(event.playlistItem.metadata.url);
    }
  }

  _attachTooltips () {
    if (!this._tooltipsInitialized) {
      this._tooltipsInitialized = true;
      if (supportsMouseInput()) {
        this.$element.addEventListener('mouseenter', this.ontooltipshow, true);
      }

      if (supportsTouchInput()) {
        this.$element.addEventListener('touchstart', this.ontooltipshow);
      }
    }
  }

  _detachTooltips () {
    if (this._tooltipsInitialized) {
      this._tooltipsInitialized = false;
      this.$element.querySelectorAll('[data-tooltip]').forEach(destroyTooltip);
      this.$element.removeEventListener('mouseenter', this.ontooltipshow, true);
      this.$element.removeEventListener('touchstart', this.ontooltipshow);
    }
  }

  /**
   * Generic handler for UI events
   *
   * @param {Object} event
   */
  handleEvent (event) {}

  /**
   * Attach layout to the dom
   *
   * @param {Element} element
   */
  attach (element) {
    this.detach();

    this.$element = element;

    if (supportsTouchInput()) {
      TouchTap.attach(this.$element);
    }

    this.$element.setAttribute('data-state', this.player.getState());
    this.theme.render();

    resizeObserver.observe(this.$element);

    // this.$element.addEventListener('click', this);
    this.$element.addEventListener('resize', this.onresize);

    this._attachTooltips();
  }

  /**
   * Detach layout from the dom
   *
   * @param {Element} element
   */
  detach () {
    if (this.$element) {
      this.$element.removeEventListener('resize', this.onresize);
      // this.$element.removeEventListener('click', this);

      resizeObserver.unobserve(this.$element);

      if (supportsTouchInput()) {
        TouchTap.detach(this.$element);
      }

      this._detachTooltips();
    }

    this.$element = undefined;
  }

  /**
   * Render layout to the dom
   */
  render () {
    return '<div></div>';
  }

  renderBranding (url = this.options.websiteUrl) {
    let brandImage = '';
    if (typeof this.options.branding === 'string') {
      brandImage = `<img src="${this.options.branding}" alt="Branding" class="iono-brand-image">`;
    } else if (this.options.branding === true) {
      brandImage = logo.replace('<svg', '<svg class="iono-brand-image"');
    } else if (url) {
      brandImage = externalLink.replace('<svg', '<svg class="iono-brand-image"');
    }

    if (brandImage && url) {
      return `<a href="${url}" target="_blank" class="iono-brand${this.options.branding ? ' iono-brand--logo' : ''}" title="${this.locale.lang('player.open')} ⇢">${brandImage}</a>`;
    }

    if (brandImage) {
      return `<div class="iono-brand${this.options.branding ? ' iono-brand--logo' : ''}">${brandImage}</div>`;
    }

    return '';
  }

  updateAdSkipButton ($button) {
    if (this.player.getAdSkipOffset() > 0) {
      const skipRemaining = iono.Player.formatSeconds(this.player.getAdSkipOffsetRemaining());

      if (this.player.isAdSkippable()) {
        $button.textContent = `${this.locale.lang('advertising.skip')} ⇢`;
        $button.removeAttribute('disabled');
      } else {
        $button.textContent = `${this.locale.lang('advertising.skip')} (${skipRemaining})`;
        $button.setAttribute('disabled', true);
      }
    } else {
      $button.setAttribute('hidden', true);
    }
  }
}
